import React from 'react';
import { ASSET_CDN_URL, MILLISECONDS_IN_AN_HOUR } from '../../Constants';
import { useNavigate } from 'react-router-dom';
import { formatDatesWithTimeZone } from '../../../helpers/DateUtils.js';
import { SHIFT_STATUSES } from '../../Constants.js';
import './BusinessShiftCard.scss';

import { EditShiftButton } from '../store/EditShiftButton.jsx';
import { DeleteShiftButton } from '../store/DeleteShiftControl.jsx';

const DateIcon = `${ASSET_CDN_URL}/shift-card/shift-date.png`;
const TimeIcon = `${ASSET_CDN_URL}/shift-card/shift-time.png`;
const LocationIcon = `${ASSET_CDN_URL}/shift-card/shift-location.png`;
const TrashCan = `${ASSET_CDN_URL}/icons/trash-icon.svg`;

const BusinessShiftCard = ({
  shift,
  select = null,
  selectLocum,
  review = null,
  showBulkSelect = false,
  bulkSelected = false,
  bulkSelectable = false,
  toggleBulkSelect = null,
  onChangeStore = null,
  store = null,
  listStoreId = null,
  clearBulkSelection = null,
  upcomingCardActions = false,
  bulkSelectionMode = false,
}) => {
  const { name, startTime, endTime, address, rate, total, unpaidBreakTime, status, locum, applicants, storeId, negotiable, calendarUrl, _id, providerNumber, billToMedicare } = shift;
  const { suburb, state, timeZone } = address;
  const { date, startTimeDisplay, endTimeDisplay } = formatDatesWithTimeZone(new Date(startTime), new Date(endTime), false, timeZone);
  const unpaidTimeDisplay = (unpaidBreakTime / MILLISECONDS_IN_AN_HOUR).toFixed(2);
  const rateDisplay = billToMedicare ? 'Rate: NA' : `$${(rate / 100).toFixed(2)}/hr`;
  const totalDisplay = billToMedicare ? 'NA' : `$${(total / 100).toFixed(2)}`;
  const canShowActions = !bulkSelectionMode;
  const showMessageButton = canShowActions && (status === SHIFT_STATUSES.CONFIRMED.key || status === SHIFT_STATUSES.FILLED.key);
  const showApplicantsButton = canShowActions && status === SHIFT_STATUSES.UNFILLED.key && applicants.length > 0;
  const showChangeStoreButton = canShowActions && showMessageButton && onChangeStore !== null;
  const showCardEdit = canShowActions
    && upcomingCardActions
    && [SHIFT_STATUSES.UNFILLED.key, SHIFT_STATUSES.FILLED.key, SHIFT_STATUSES.CONFIRMED.key].includes(status)
    && startTime > Date.now()
    && store !== null;
  const showCardDelete = canShowActions && upcomingCardActions && (status === SHIFT_STATUSES.UNFILLED.key || status === SHIFT_STATUSES.FILLED.key || status === SHIFT_STATUSES.CONFIRMED.key) && listStoreId;
  const showManagementActions = showCardEdit || showChangeStoreButton || showCardDelete;
  const bulkContextText = status === SHIFT_STATUSES.UNFILLED.key
    ? `${applicants.length} applicant${applicants.length === 1 ? '' : 's'}`
    : locum ? `${locum.firstName} ${locum.lastName}` : 'Assigned locum';
  const navigate = useNavigate();
  const viewMore = select !== null ? () => select(shift) : () => navigate(`/find/shift?id=${shift._id}`);
  const viewLocum = () => selectLocum(locum, shift);
  const sendMessage = () => navigate('/user/messages', { state: { open: false, locum, store: { name, _id: storeId } }});
  const viewApplicants = () => review !== null
    ? review(shift)
    : navigate('/store/applicants/review', { state: { storeId: shift.storeId, shift, locum: null} });
  const changeStore = () => onChangeStore(shift);
  const { className, text, hasSubtitle, subtitle } = SHIFT_STATUSES[status];
  const downloadCalendar = () => {
    if (calendarUrl) {
      const downloadElem = document.createElement('a');
      downloadElem.setAttribute('href', calendarUrl);
      downloadElem.setAttribute('download', `shift-${_id}.ics`);
      downloadElem.style.display = 'none';
      document.body.appendChild(downloadElem);
      downloadElem.click();
      document.body.removeChild(downloadElem);
    }
  };
  const toggleSelection = (e) => {
    e.stopPropagation();
    if (bulkSelectable && toggleBulkSelect !== null) {
      toggleBulkSelect(shift);
    }
  };
  const handleCardKeyDown = (e) => {
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (bulkSelectionMode) {
        toggleSelection(e);
        return;
      }
      viewMore();
    }
  };
  const handleButtonClick = (callback) => (e) => {
    e.stopPropagation();
    callback();
  };
  const onCardClick = bulkSelectionMode ? toggleSelection : viewMore;
  return (
    <li className={`shift-card business ${bulkSelected ? 'bulk-selected' : ''} ${bulkSelectionMode ? 'bulk-selection-mode' : ''}`} onClick={onCardClick} onKeyDown={handleCardKeyDown} role={'button'} tabIndex={0}>
      {showBulkSelect &&
        <label className={`bulk-select-checkbox ${bulkSelectable ? '' : 'disabled'}`} onClick={(e) => e.stopPropagation()}>
          <input
            type={'checkbox'}
            checked={bulkSelected}
            disabled={!bulkSelectable}
            onChange={toggleSelection}
            aria-label={`Select ${date} shift for bulk actions`}
          />
        </label>
      }
      { showCardDelete &&
        <DeleteShiftButton
          shiftId={_id}
          listStoreId={listStoreId}
          onAfterDelete={clearBulkSelection || undefined}
          buttonClassName={'delete-shift-corner very-dark'}
        >
          <img className={'delete-icon'} src={TrashCan} alt={'Delete shift'} />
        </DeleteShiftButton>
      }
      <section className={'shift-details'}>
        <section className={`${className} status`}>
          {text}
          {hasSubtitle && <span className={'subtitle'}>{subtitle}</span>}
        </section>
        <h2 className={'store-name'}>{name}</h2>
        <span className={'shift-date'}>
          <img className={'shift-icon'} src={DateIcon} alt={'Date icon'} />
          {date}
          
          { calendarUrl && canShowActions
            ? <button className={'add-to-calendar very-light'} onClick={handleButtonClick(downloadCalendar)}>
                {'Add to calendar'}
              </button>
            : null
          }
        </span>
        <span className={'shift-time'}>
          <img className={'shift-icon'} src={TimeIcon} alt={'Time icon'} />
          {`${startTimeDisplay} - ${endTimeDisplay}`}
        </span>
        <span className={'shift-location'}>
          <img className={'shift-icon'} src={LocationIcon} alt={'Location icon'} />
          {`${suburb}, ${state}`}
        </span>
        <span className={'shift-break-time'}>{`Unpaid break: ${unpaidTimeDisplay} hrs`}</span>
        <span className={'shift-rate'}>{rateDisplay}</span>
        <span className={'shift-total'}>{`Total: ${totalDisplay}`}</span>
        { billToMedicare && <span className={'shift-rate'}>{'Locum takes home Medicare billing'}</span>}
        { !billToMedicare && 
          <span className={`shift-negotiable ${negotiable ? 'negotiable' : ''}`}>{negotiable ? 'Rate Negotiable' : 'Rate Fixed'}</span>
        }
        { !!providerNumber && <span className={'shift-provider-number'}>{`Provider Number: ${providerNumber}`}</span>}
      </section>
      <section className={'shift-actions'}>
        {bulkSelectionMode &&
          <section className={`bulk-selection-summary ${bulkSelected ? 'selected' : ''} ${bulkSelectable ? '' : 'disabled'}`}>
            <span className={'bulk-selection-state'}>{bulkSelected ? 'Selected' : (bulkSelectable ? 'Available' : 'Not available')}</span>
            <span className={'bulk-selection-context'}>{bulkContextText}</span>
            <span className={'bulk-selection-hint'}>{bulkSelectable ? 'Click card to select' : 'Cannot be selected for this action'}</span>
          </section>
        }
        <section className={'shift-buttons'}>
          {showMessageButton && <span className={'shift-locum'}>{`Your Locum is ${locum.firstName} ${locum.lastName}`}</span>}
          {showApplicantsButton && 
            <span className={'shift-applicants'}>
            {`${applicants.length} Applicant${applicants.length === 1 ? '' : 's'}`}
            </span>
          }
          { showMessageButton &&       
            <button className={'shift-button view-locum-button very-light'} onClick={handleButtonClick(viewLocum)}>
              {`Locum Profile`}
            </button>
          }
          { showMessageButton &&       
            <button className={'shift-button message-locum-button very-dark'} onClick={handleButtonClick(sendMessage)}>
              {`Message Locum`}
            </button>
          }
          { showApplicantsButton &&       
            <button className={'shift-button view-applicants-button very-light'} onClick={handleButtonClick(viewApplicants)}>
              {`View Applicants`}
            </button>
          }
        </section>
        { showManagementActions &&
          <section className={'shift-management-actions'} onClick={(e) => e.stopPropagation()}>
            { showCardEdit &&
              <EditShiftButton shift={shift} editText={'Edit shift'} select={() => {}} classNames={['shift-button', 'edit-shift-card']} store={store} />
            }
            { showChangeStoreButton &&
              <button className={'shift-button change-store-button very-light'} onClick={handleButtonClick(changeStore)}>
                {'Change store'}
              </button>
            }
          </section>
        }
      </section>
    </li>
  );
};

export default BusinessShiftCard;
